import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, useModal } from "../../modal";
import {
  Button,
  TableContainer,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  GridContainer,
  GridItem,
  DataCard,
  DataCardHeader,
  DataCardTitle,
} from "./PeriodicAcquirerReport.styles";
import PeriodicAcquirerReportBaseData from "./PeriodicAcquirerReport.baseData";
import { AccountBalance, Business } from "@mui/icons-material";
import PeriodicAcquirerReportHoldings from "./PeriodicAcquirerReport.holdings";
import { ResetStore } from "./PeriodicAcquirerReport.slice";

const formatDecimal = (value) => {
  if (typeof value === "string") {
    const cleanedValue = value.replace(",", ".").replace(/[^0-9.]/g, "");
    if ((cleanedValue.match(/\./g) || []).length > 1) return "0.0000";
    const number = parseFloat(cleanedValue);
    return !isNaN(number) ? number.toFixed(4) : "0.0000";
  }
  if (typeof value === "number") return value.toFixed(4);
  return "0.0000";
};

const formatDate = (rawDate) => {
  const year = rawDate.toString().slice(0, 4);
  const month = rawDate.toString().slice(4, 6);
  const day = rawDate.toString().slice(6, 8);
  return `${month}/${day}/${year}`;
};

const PeriodicAcquirerReportResultModal = () => {
  const dispatch = useDispatch();
  const { close } = useModal();
  const { isOpen } = useSelector((state) => state.modal);
  const {
    acquirerID,
    acquirerName,
    consolidatedData,
    isos,
  } = useSelector((state) => state.periodicAcquirerReport);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    close();
    dispatch(ResetStore());
  };

  const downloadCSV = () => {
    const headers = [
      "ISO ID",
      "ISO Name",
      "Date",
      "Holding Name",
      "Holding ID",
      "Holding Settlement",
      "Holding Amount",
      "Holding Trans. Count",
      "Holding MDR Fee",
      "Holding Proc. Fee",
      "Brand Name",
      "Brand ID",
      "Brand Settlement",
      "Brand Amount",
      "Brand Trans. Count",
      "Brand MDR Fee",
      "Brand Proc. Fee",
      "MID",
      "Merchant Name",
      "Merchant Store",
      "Merchant Settlement",
      "Merchant Amount",
      "Merchant Trans. Count",
      "Merchant MDR Fee",
      "Merchant Proc. Fee",
      "Terminal ID",
      "Terminal Exchange Name",
      "Terminal Settlement",
      "Terminal Amount",
      "Terminal Trans. Count",
      "Terminal MDR Fee",
      "Terminal Proc. Fee",
    ];

    const rows = [];
    const isosList = Array.isArray(isos) ? isos : [];

    isosList.forEach((iso) => {
      const isoID = iso?.isoID ?? "";
      const isoName = iso?.isoName ?? "";
      const dates = iso?.holding?.dates;
      const datesList = Array.isArray(dates) ? dates : [];

      datesList.forEach((entry) => {
        const date = formatDate(entry.date);
        const holdings = entry.holdings ?? entry;
        const holdingAmountDue = formatDecimal(holdings?.amountDue);
        const holdingTxAmount = formatDecimal(holdings?.transactionsAmount);
        const holdingTxCount = holdings?.transactionsCount ?? "";
        const holdingTxMDR = formatDecimal(holdings?.txMDR);
        const holdingTxProcessing = formatDecimal(holdings?.txProcessing);

        const groups = holdings?.groups?.group;
        const groupsList = Array.isArray(groups) ? groups : groups ? [groups] : [];

        groupsList.forEach((group) => {
          const merchants = group.merchants?.merchant;
          const merchantsList = Array.isArray(merchants)
            ? merchants
            : merchants
            ? [merchants]
            : [];

          merchantsList.forEach((merchant) => {
            const terminals = merchant.terminals?.terminal;
            const terminalsList = Array.isArray(terminals)
              ? terminals
              : terminals
              ? [terminals]
              : [];

            if (terminalsList.length > 0) {
              terminalsList.forEach((terminal) => {
                rows.push([
                  isoID,
                  isoName,
                  date,
                  holdings?.merchantHolding ?? "",
                  holdings?.merchantHoldingID ?? "",
                  holdingAmountDue,
                  holdingTxAmount,
                  holdingTxCount,
                  holdingTxMDR,
                  holdingTxProcessing,
                  group.merchantGroup ?? "",
                  group.merchantGroupID ?? "",
                  formatDecimal(group.amountDue),
                  formatDecimal(group.transactionsAmount),
                  group.transactionsCount,
                  formatDecimal(group.txMDR),
                  formatDecimal(group.txProcessing),
                  merchant.name ?? "",
                  merchant.ID ?? "",
                  merchant.store ?? "",
                  formatDecimal(merchant.amountDue),
                  formatDecimal(merchant.transactionsAmount),
                  merchant.transactionsCount,
                  formatDecimal(merchant.txMDR),
                  formatDecimal(merchant.txProcessing),
                  terminal.terminalID ?? "",
                  terminal.exchangeName ?? "N/A",
                  formatDecimal(terminal.amountDue),
                  formatDecimal(terminal.transactionsAmount),
                  terminal.transactionsCount,
                  formatDecimal(terminal.txMDR),
                  formatDecimal(terminal.txProcessing),
                ]);
              });
            } else {
              rows.push([
                isoID,
                isoName,
                date,
                holdings?.merchantHolding ?? "",
                holdings?.merchantHoldingID ?? "",
                holdingAmountDue,
                holdingTxAmount,
                holdingTxCount,
                holdingTxMDR,
                holdingTxProcessing,
                group.merchantGroup ?? "",
                group.merchantGroupID ?? "",
                formatDecimal(group.amountDue),
                formatDecimal(group.transactionsAmount),
                group.transactionsCount,
                formatDecimal(group.txMDR),
                formatDecimal(group.txProcessing),
                merchant.name ?? "",
                merchant.ID ?? "",
                merchant.store ?? "",
                formatDecimal(merchant.amountDue),
                formatDecimal(merchant.transactionsAmount),
                merchant.transactionsCount,
                formatDecimal(merchant.txMDR),
                formatDecimal(merchant.txProcessing),
                "",
                "",
                "",
                "",
                "",
                "",
                "",
              ]);
            }
          });

          if (merchantsList.length === 0) {
            rows.push([
              isoID,
              isoName,
              date,
              holdings?.merchantHolding ?? "",
              holdings?.merchantHoldingID ?? "",
              holdingAmountDue,
              holdingTxAmount,
              holdingTxCount,
              holdingTxMDR,
              holdingTxProcessing,
              group.merchantGroup ?? "",
              group.merchantGroupID ?? "",
              formatDecimal(group.amountDue),
              formatDecimal(group.transactionsAmount),
              group.transactionsCount,
              formatDecimal(group.txMDR),
              formatDecimal(group.txProcessing),
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
              "",
            ]);
          }
        });

        if (groupsList.length === 0) {
          rows.push([
            isoID,
            isoName,
            date,
            holdings?.merchantHolding ?? "",
            holdings?.merchantHoldingID ?? "",
            holdingAmountDue,
            holdingTxAmount,
            holdingTxCount,
            holdingTxMDR,
            holdingTxProcessing,
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
          ]);
        }
      });
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "acquirer_iso_date_range_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      fullScreen
      actions={
        <>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
          <Button onClick={downloadCSV} variant="contained">
            Download CSV
          </Button>
        </>
      }
    >
      <GridContainer>
        <GridItem xs={12}>
          <PeriodicAcquirerReportBaseData
            acquirerID={acquirerID}
            acquirerName={acquirerName}
            consolidatedData={consolidatedData}
          />
        </GridItem>
        <GridItem xs={12}>
          <GridContainer spacing={2}>
            {Array.isArray(isos) && isos.length > 0 ? (
              isos.map((iso, index) => {
                const dates = iso?.holding?.dates;
                const holdingList = Array.isArray(dates) ? dates : [];
                return (
                  <GridItem xs={12} key={`iso-${iso?.isoID ?? index}-${index}`}>
                    <Accordion defaultExpanded={index === 0}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`iso-${iso?.isoID}-content`}
                        id={`iso-${iso?.isoID}-header`}
                      >
                        <Business color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                          {iso?.isoName ?? iso?.isoID ?? `ISO ${index + 1}`} ({iso?.isoID ?? ""})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <DataCard>
                          <DataCardHeader>
                            <AccountBalance color="primary" />
                            <DataCardTitle>Holding by date</DataCardTitle>
                          </DataCardHeader>
                          <TableContainer component={Paper}>
                            <PeriodicAcquirerReportHoldings
                              holdingList={holdingList}
                            />
                          </TableContainer>
                        </DataCard>
                      </AccordionDetails>
                    </Accordion>
                  </GridItem>
                );
              })
            ) : (
              <GridItem xs={12}>
                <Typography variant="body2" color="text.secondary">
                  No ISO data available for this period.
                </Typography>
              </GridItem>
            )}
          </GridContainer>
        </GridItem>
      </GridContainer>
    </Modal>
  );
};

export default PeriodicAcquirerReportResultModal;
